/**
 * Service layer for AI location recommendation API interactions
 * Handles streaming recommendations and adding locations to dashboards
 */

import { getLocationId } from "../../../utils/score-insights/ai/aiLocationMatchers";
import { mergeNotesIntoLocations, parseNotesFromAiText } from "../../../utils/score-insights/ai/aiLocationParsers";
import { addAILocations, streamDashboardAnalysis } from "./dashboards-crud.services";

/**
 * Stream AI location recommendations from the backend
 * Handles the streaming response, parsing, and extracting location data with notes
 *
 */
export const streamLocationRecommendations = async ({ dashboardData, token, chatFunctions }) => {
	// Prepare the conversation message
	const conversationMessages = [
		{
			role: "user",
			content:
				"Please recommend new locations for my business expansion based on my current dashboard configuration.",
		},
	];

	// Add user message to chat UI
	chatFunctions.addMessage({
		role: "user",
		content:
			"Please recommend new locations for my business expansion based on my current dashboard configuration.",
	});

	chatFunctions.clearStreamed();
	const response = await streamDashboardAnalysis({
		messages: conversationMessages,
		dashboardData: {
			dashboard_type: "score_insights",
			type: "score_insights",
			dashboard_id: dashboardData.dashboard_id,
			soc_codes: dashboardData.soc_codes,
			location_ids: dashboardData.location_ids,
			target_wage: dashboardData.target_wage,
			drive_time: dashboardData.drive_time,
			soc_occupation_quantities: dashboardData.soc_occupation_quantities,
		},
		token: token,
	});
	const reader = response.body.getReader();

	const decoder = new TextDecoder("utf-8");

	let buffer = "";
	let assistantText = "";
	let capturedLocations = [];

	// Process streaming chunks
	while (true) {
		const { value, done } = await reader.read();
		if (done) break;

		buffer += decoder.decode(value, { stream: true });
		const events = buffer.split("\n\n");
		buffer = events.pop() || "";

		for (const event of events) {
			const line = event.trim();
			if (!line.startsWith("data:")) continue;

			const jsonText = line.slice(5).trim();
			if (!jsonText) continue;

			let data;
			try {
				data = JSON.parse(jsonText);
			} catch (parseErr) {
				console.error("Failed to parse SSE chunk:", parseErr, jsonText);
				continue;
			}

			if (data.type === "suggested_locations" && data.locations) {
				capturedLocations = data.locations;
			}

			// Stream text content to chat UI
			if (data.content) {
				assistantText += data.content;
				chatFunctions.addStreamedMessage(assistantText);
			}

			if (data.role === "system" && data.content) {
				chatFunctions.setError(data.content);
			}
		}
	}

	if (!assistantText) {
		throw new Error("No response received from AI. Please try again later or contact support.");
	}

	const parsedNotes = parseNotesFromAiText(assistantText);

	let finalLocations = [];

	if (capturedLocations.length > 0 && parsedNotes.length > 0) {
		const locationsWithNotes = mergeNotesIntoLocations(capturedLocations, parsedNotes);
		finalLocations = locationsWithNotes.map((loc) => ({
			...loc,
			_id: loc._id || loc.location_id,
		}));
	} else if (capturedLocations.length > 0) {
		finalLocations = capturedLocations.map((loc) => ({
			...loc,
			_id: loc._id || loc.location_id,
		}));
	}

	chatFunctions.addMessage({ role: "assistant", content: assistantText });
	chatFunctions.clearStreamed();
	chatFunctions.setLoading(false);

	return {
		locations: finalLocations,
		assistantMessage: assistantText,
	};
};

/**
 * Add AI-recommended locations to a dashboard
 * Sends location IDs to backend to be added to the user's dashboard
 *
 */
export const addLocationsToMap = async ({ dashboardId, locations }) => {
	if (!dashboardId) {
		throw new Error("Dashboard ID is required");
	}

	if (!locations || locations.length === 0) {
		throw new Error("At least one location is required");
	}

	const locationPayload = locations.map((loc) => ({
		location_id: getLocationId(loc),
	}));

	const validPayload = locationPayload.filter((item) => item.location_id !== null);

	if (validPayload.length === 0) {
		throw new Error("No valid location IDs found");
	}

	const response = await addAILocations({
		dashboard_id: dashboardId,
		locations: validPayload,
	});

	if (response?.status !== "success") {
		throw new Error(response?.message || "Failed to add AI locations to dashboard");
	}

	return response;
};
